import { useReduxState } from 'hooks';
import { useApiData } from 'lib/api';
import {
  CalendarEventDateIcon,
  CalendarEventDateString
} from 'components/calendar';
import {
  Badge,
  EmptyIndicator,
  FlexContainer,
  LoadingIndicator,
  TitleWithIcon,
  User
} from 'components/common';
import { Box, BoxBackLink, BoxRow } from 'components/common/box';
import { ParticipantsStats } from 'components/common/stats';
import {
  MyTeamFollowBox,
  useMyTeamFollowContext,
  useTeamUserList
} from '.';

import type { GetTeamCalendarEventsRes } from 'shared-types/responses';

const MyTeamCalendarEventsEvent = () => {
  const { messages, user } = useReduxState();

  const { queryParams } = useMyTeamFollowContext();

  const calendarEventId = queryParams['calendarEventId'] as string;

  // Fetch only on the event id so changing the user-list filters
  // (page/search/participantsOnly) never refetches — the single-event
  // response already carries every supervised user, so filtering is local.
  const { data, loading } = useApiData<GetTeamCalendarEventsRes>({
    url: `/admin/supervisors/${user.id}/team-calendar-events?calendarEventId=${calendarEventId}`,
    onlyIf: Boolean(user?.id && calendarEventId)
  });

  const calendarEvent = data?.calendarEvents?.[0];

  const { numberOfPages, participantsOnly, users } = useTeamUserList({
    isParticipant: (user) =>
      Boolean(calendarEvent?.progressData?.progressDataByUser?.[user.id]),
    supervisedUsers: data?.supervisedUsers || []
  });

  return loading && !calendarEvent ? (
    <LoadingIndicator />
  ) : (
    <>
      <Box
        title={
          <BoxBackLink
            as="/my-team/follow/calendar-events"
            href="/my-team/follow/calendar-events"
            text={messages.commonAllCalendarEvents}
          />
        }
      >
        {calendarEvent ? (
          <div className="row-sm row-center">
            <div className="col">
              <FlexContainer style={{ gap: 15, minWidth: 250 }}>
                <CalendarEventDateIcon
                  startDate={calendarEvent.startDate}
                  useMonthSpecifier={true}
                />

                <TitleWithIcon
                  title={calendarEvent.title}
                  titleLink={{
                    as: `/my-team/follow/calendar-events/${calendarEvent.id}`,
                    href: '/my-team/follow/calendar-events/[calendarEventId]'
                  }}
                >
                  <CalendarEventDateString
                    startDate={calendarEvent.startDate}
                    endDate={calendarEvent.endDate}
                  />
                </TitleWithIcon>
              </FlexContainer>
            </div>
            <div className="col-min">
              <ParticipantsStats
                completedLabel={messages.eventCalendarParticipantParticipated}
                completionEnabled={true}
                inProgressLabel={messages.commonAdded}
                notStartedLabel={messages.commonNotAdded}
                progressData={{
                  completed: calendarEvent.progressData?.participated,
                  started: calendarEvent.progressData?.added,
                  // participantsOnly narrows the bar max to the users added to
                  // the event; participated/added are unchanged.
                  participants: participantsOnly
                    ? calendarEvent.progressData?.added
                    : calendarEvent.progressData?.users
                }}
                showNotStarted={!participantsOnly}
              />
            </div>
          </div>
        ) : null}
      </Box>

      <MyTeamFollowBox
        numberOfPages={numberOfPages}
        style={{ marginTop: 20 }}
        title={messages.commonUsers}
      >
        {loading ? (
          <LoadingIndicator />
        ) : users?.length ? (
          users.map((user) => (
            <BoxRow key={user.id}>
              <div className="row-sm row-center">
                <div className="col">
                  <User enableProfileLink={true} user={user} />
                </div>
                <div className="col-min">
                  {calendarEvent.progressData?.progressDataByUser[user.id]
                    ?.participated ? (
                    <Badge
                      category="success-light"
                      icon="check"
                      isRounded={false}
                      text={messages.eventCalendarParticipantParticipated}
                    />
                  ) : calendarEvent.progressData?.progressDataByUser[user.id]
                      ?.status === 'added' ? (
                    <Badge
                      icon="check"
                      isRounded={false}
                      text={messages.commonAdded}
                    />
                  ) : (
                    <Badge isRounded={false} text={messages.commonNotAdded} />
                  )}
                </div>
              </div>
            </BoxRow>
          ))
        ) : (
          <EmptyIndicator icon="user" title={messages.commonNoUsers} />
        )}
      </MyTeamFollowBox>
    </>
  );
};

export default MyTeamCalendarEventsEvent;
